import { Component, OnInit, OnDestroy, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { ServicesFormService, TaskDetailsResponse, TaskSummary, TaskRequest } from '../../services/services-form.service';
import { RequestAction } from '../../models/form-models';
import { TaskInitialFormTabComponent } from './task-initial-form-tab/task-initial-form-tab.component';
import { TaskFormTabComponent } from './task-form-tab/task-form-tab.component';
import { TaskHistoryTabComponent } from './task-history-tab/task-history-tab.component';

/**
 * Task Viewer Component - Parent/Orchestrator
 * Manages three tab components: Initial Form, Task Form, History
 * Conditional rendering and readonly state based on pageType
 * Reusable across Inbox, Sent, Completed, MyRequests pages
 */
@Component({
  selector: 'app-task-viewer',
  standalone: true,
  templateUrl: './task-viewer.component.html',
  styleUrls: ['./task-viewer.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    TaskInitialFormTabComponent,
    TaskFormTabComponent,
    TaskHistoryTabComponent
  ]
})
export class TaskViewerComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private formService = inject(ServicesFormService);
  private translate = inject(TranslateService);

  // Page type mapping from numeric route parameter
  private readonly pageTypeMap: { [key: number]: 'inbox' | 'sent' | 'completed' | 'myRequests' } = {
    0: 'inbox',
    1: 'sent',
    2: 'completed',
    3: 'myRequests'
  };

  // Reverse mapping for navigation
  private readonly reversePageTypeMap: { [key: string]: number } = {
    'inbox': 0,
    'sent': 1,
    'completed': 2,
    'myRequests': 3
  };

  // Outputs
  @Output() taskUpdated = new EventEmitter<TaskDetailsResponse>();
  @Output() taskClosed = new EventEmitter<void>();

  // Tab management
  activeTab: 'initialForm' | 'form' | 'history' = 'initialForm';
  slideDirection: 'left' | 'right' | 'none' = 'none';

  // Task data
  taskData: TaskDetailsResponse | null = null;
  summary: TaskSummary | null = null;
  taskRequest: TaskRequest | null = null;
  initialFormSchema: string = '';
  taskFormSchema: string = '';
  availableActions: string[] = [];
  previousActions: RequestAction[] = [];
  requestTypeLogicalId: string = '';
  initialFormData: string = '';
  requestId: string | null = null;
  taskId: string | null = null;
  pageType: 'inbox' | 'sent' | 'completed' | 'myRequests' = 'inbox';
  delegationId?: number;

  // UI state
  isLoading: boolean = true;
  errorMessage: string = '';
  successMessage: string = '';
  isReadonly: boolean = false;
  currentLanguage: string = 'en';

  // Lifecycle
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Subscribe to route parameters to get requestId, taskId, and pageType
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const pageTypeNum = parseInt(params['pageType'], 10);
      this.pageType = this.pageTypeMap[pageTypeNum] || 'inbox';
      this.requestId = params['requestId'];
      this.taskId = params['taskId'];
      
      // Tab order is Initial Form, Action, History; every page type starts on the initial form
      this.activeTab = 'initialForm';
      
      console.log('[TaskViewer] Route params loaded:', {
        pageType: this.pageType,
        requestId: this.requestId,
        taskId: this.taskId,
        activeTab: this.activeTab
      });

      // Load task when route params are available
      if (this.requestId && this.taskId) {
        this.loadTask();
      } else {
        this.errorMessage = 'Missing requestId or taskId in route parameters';
        this.isLoading = false;
      }
    });

    // Setup language change listener
    this.currentLanguage = this.translate.currentLang || 'en';
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(event => {
      this.currentLanguage = event.lang;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load task details from API
   */
  loadTask(): void {
    if (!this.requestId || !this.taskId) {
      this.errorMessage = 'Missing requestId or taskId';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.formService
      .getTaskDetails(this.requestId, this.taskId, this.delegationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: TaskDetailsResponse) => {
          this.processTaskData(response);
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error('[TaskViewer] Error loading task:', error);
          this.errorMessage = 'Failed to load task details';
          this.isLoading = false;
        }
      });
  }

  /**
   * Process task API response
   */
  private processTaskData(response: TaskDetailsResponse): void {
    try {
      this.taskData = response;
      this.summary = response.request?.summary || null;
      this.previousActions = response.request?.previousActions || [];
      this.availableActions = response.request?.availableActions || [];

      if (response.request) {
        this.taskRequest = response.request;
        this.requestTypeLogicalId = response.request.details?.requestTypeLogicalId || '';
        this.taskFormSchema = response.request.metadata || '';
        this.initialFormData = response.request.details?.formData || '';
      }

      // Log status for debugging
      console.log('[TaskViewer] Request status:', this.summary?.status, 'PageType:', this.pageType);
      console.log('[TaskViewer] RequestTypeLogicalId:', this.requestTypeLogicalId);
      console.log('[TaskViewer] InitialFormData length:', this.initialFormData?.length);
      console.log('[TaskViewer] API isReadonly:', this.taskRequest?.isReadonly);

      // Determine readonly state based on pageType
      this.setReadonlyState();

      console.log('[TaskViewer] Task data processed, isReadonly:', this.isReadonly);
      this.taskUpdated.emit(response);
    } catch (error) {
      console.error('[TaskViewer] Error processing task data:', error);
      this.errorMessage = 'Failed to process task data';
    }
  }

  /**
   * Set readonly state based on pageType
   */
  private setReadonlyState(): void {
    // Sent and Completed: always readonly
    if (this.pageType === 'sent' || this.pageType === 'completed') {
      this.isReadonly = true;
    }
    // MyRequests: readonly except for Draft
    else if (this.pageType === 'myRequests') {
      this.isReadonly = this.summary?.status !== 'Draft';
    }
    // Inbox: editable only when the API allows edit AND there are workflow actions
    // available (matches frontend behavior in task.component.ts ~line 236-244).
    // If isReadonly is missing on the response, default to readonly for safety.
    else if (this.pageType === 'inbox') {
      const apiReadonly = this.taskRequest?.isReadonly ?? true;
      const hasActions = (this.availableActions?.length ?? 0) > 0;
      this.isReadonly = apiReadonly || !hasActions;
    }
    // Default: editable
    else {
      this.isReadonly = false;
    }
  }

  /**
   * Change active tab
   */
  selectTab(tabName: 'initialForm' | 'form' | 'history'): void {
    if (tabName === this.activeTab) return;

    const tabOrder = ['initialForm', 'form', 'history'];
    const currentIndex = tabOrder.indexOf(this.activeTab as string);
    const newIndex = tabOrder.indexOf(tabName);
    this.slideDirection = newIndex > currentIndex ? 'right' : 'left';
    this.activeTab = tabName;

    setTimeout(() => {
      this.slideDirection = 'none';
    }, 300);
  }

  /**
   * Handle action execution from task-form-tab
   */
  onActionExecuted(event: { action: string; formData: any }): void {
    console.log('[TaskViewer] Action executed:', event.action);
    this.successMessage = `Action "${event.action}" completed successfully`;
    setTimeout(() => {
      this.loadTask();
      this.successMessage = '';
    }, 1500);
  }

  /**
   * Close task viewer and navigate back to the appropriate page
   */
  closeTask(): void {
    const pageTypeNum = this.reversePageTypeMap[this.pageType];
    const routes: { [key: string]: string } = {
      'inbox': '/requests/inbox',
      'sent': '/requests/sent',
      'completed': '/requests/completed',
      'myRequests': '/requests/my-sent'
    };

    const targetRoute = routes[this.pageType];
    this.taskClosed.emit();
    this.router.navigate([targetRoute]);
  }

  /**
   * Get direction for RTL/LTR
   */
  getDir(): string {
    return this.currentLanguage === 'ar' ? 'rtl' : 'ltr';
  }

  /**
   * Check if all fields should be disabled based on page type
   * Returns true for sent, completed, and myRequests pages
   * This controls readonly state of form inputs
   */
  get allFieldsDisabled(): boolean {
    return this.pageType === 'sent' || this.pageType === 'completed' || this.pageType === 'myRequests';
  }

  /**
   * Check if tabs should be clickable/navigable
   * Returns false only for sent and completed (fully readonly pages)
   * MyRequests allows viewing initial form and history, just not editing
   */
  get areTabsNavigable(): boolean {
    return true;
  }

  /**
   * Check if action buttons should be shown (only for inbox)
   */
  get showActionButtons(): boolean {
    return this.pageType === 'inbox';
  }

  /**
   * Check if task form tab should be shown
   * Hide for myRequests page
   */
  get showTaskFormTab(): boolean {
    return this.pageType !== 'myRequests' && this.pageType !== 'completed';
  }
}
